import { PUZZLE_LEVEL_COUNT } from './puzzleMeta'

/**
 * 🌟 The economy contract (v4.92) — the single source of truth for every
 * stardust amount in the game, shared by the client (display + optimistic
 * estimates) and the server (the ONLY authority that actually credits).
 *
 * Per Javi (2026-08-16): "stardust being cheatable defeats the whole
 * purpose of the game." The wallet moved server-side: the client reports
 * EVENTS ("act 3 cleared in run X"), never amounts — the server computes
 * the payout from these tables, admits each event key once, and rate-caps
 * every faucet at honest-player speed. This file must therefore have NO
 * app imports (Nitro bundles it); app/config/tuning.ts re-exposes these
 * numbers so game code keeps reading TUNING as before.
 */

// ── Payout tables (moved verbatim from useStardust / tuning v4.91) ──────

/** Payout for beating the act-N boss (index act−1). */
export const STARDUST_PER_ACT = [15, 30, 60]

/** Ascension multiplier: +30% per level, so A10 pays 4× Normal. */
export function stardustAscensionMult(ascension: number): number {
  return 1 + 0.3 * Math.max(0, ascension)
}

/** ⚔️ Gauntlet acts past 3 pay the act-3 rate plus this share of it per
 * extra act. v5.04 (per Javi: "slightly reduce" gauntlet income):
 * 0.10 → 0.07 — act 8 pays 324 instead of 360 at the Gauntlet's
 * ascension, act 13 ~408 instead of 480; acts 1–3 untouched. */
export const GAUNTLET_STARDUST_RAMP = 0.07

export function stardustForAct(act: number, ascension = 0): number {
  const base = STARDUST_PER_ACT[Math.max(0, Math.min(STARDUST_PER_ACT.length, act) - 1)] ?? 0
  const ramp = 1 + GAUNTLET_STARDUST_RAMP * Math.max(0, act - STARDUST_PER_ACT.length)
  return Math.round(base * ramp * stardustAscensionMult(ascension))
}

/** 🏅 Ascension first-clear bounty: ⭐ × level, once per level ever.
 * v5.16 (per Javi): halved 100 → 50 — the whole career now injects
 * 2,800⭐ (was 5,600), so climbing pays the ladder, not the bank. */
export const FIRST_CLEAR_STARDUST = 50

export function firstClearStardust(ascension: number): number {
  const level = Math.max(1, Math.floor(Number.isFinite(ascension) ? ascension : 0))
  return level * FIRST_CLEAR_STARDUST
}

/** 🧩 Puzzle first-clear: 1.5⭐ × level (rounded up), floor 5, once
 * ever. v5.16 (per Javi): halved alongside the ascension bounty so the
 * ladder (1,930 total) stays UNDER the ascension career (2,800) — the
 * v3.76 rule that one-time content is a head start, not the economy. */
export const PUZZLE_CLEAR_STARDUST = 1.5
export const PUZZLE_CLEAR_FLOOR = 5

export function puzzleClearStardust(level: number): number {
  const lv = Math.max(1, Math.floor(level))
  return Math.max(PUZZLE_CLEAR_FLOOR, Math.ceil(lv * PUZZLE_CLEAR_STARDUST))
}

/** 🌟 Shop price per rarity (v3.62 economy pass). */
export const CREATURE_PRICES: Record<string, number> = {
  uncommon: 150, rare: 380, epic: 1000, legendary: 2500,
}

/** 🎲 Shelf/booster roll weights (v2.75, flatter than the draft so the
 * premium tiers are actually visible). Shared since v4.93: the server
 * rolls packs from these, and the in-shop odds disclosure derives from
 * the same numbers. */
export const SHOP_WEIGHTS: Record<string, number> = {
  uncommon: 100, rare: 45, epic: 20, legendary: 8,
}

/** 🎴 Booster pack: 4 cards at 700⭐; dupes refund a share of their own
 * rarity's price (rungs 53 / 152 / 450 / 1,400 — legendary = two packs). */
/** 🎓 v7.04 (per Javi): finishing the First Dispatch pays a booster's worth. */
export const TUTORIAL_STARDUST = 700
export const BOOSTER = {
  cards: 4,
  price: 700,
  dupeRefundPct: { uncommon: 0.35, rare: 0.4, epic: 0.45, legendary: 0.56 } as Record<string, number>,
}

export function boosterDupeRefund(rarity: string | undefined): number {
  const pct = BOOSTER.dupeRefundPct[rarity ?? 'uncommon'] ?? BOOSTER.dupeRefundPct.uncommon ?? 0
  const price = CREATURE_PRICES[rarity ?? 'uncommon'] ?? CREATURE_PRICES.uncommon ?? 0
  // round, not ceil: 2500 × 0.56 floats to 1400.0000000000002 and the
  // legendary rung must land EXACTLY on two pack prices (v4.34).
  return Math.round(price * pct)
}

/** The most a single pack can refund: four legendary dupes. */
export function boosterMaxRefund(): number {
  return BOOSTER.cards * boosterDupeRefund('legendary')
}

/** 📅 Daily Silhouette payouts (flat trickles by design). */
export const DAILY_ECONOMY = {
  guessRewards: [10, 6, 3] as readonly number[],
  missReward: 1,
  statBonus: 5,
  milestones: { 3: 15, 5: 25, 7: 50, 10: 75, 14: 100, 21: 150 } as Record<number, number>,
  boosterEvery: 30,
}

export function dailyGuessReward(guessesUsed: number, solved: boolean): number {
  if (!solved) return DAILY_ECONOMY.missReward
  return DAILY_ECONOMY.guessRewards[guessesUsed - 1] ?? DAILY_ECONOMY.missReward
}

// ── 💳 Real-money stardust (v4.94, Stripe on web) ───────────────────────

/**
 * The purchasable packs. Server-only truth for what a checkout may
 * charge and credit — the client renders these, never proposes them.
 * Anchors: a booster is 700⭐, a legendary 2,500⭐, an A10 climb banks
 * 660⭐. Value-per-dollar climbs gently with pack size (up to ~+28%).
 * PRICES ARE A PRODUCT KNOB — tune here, ship, done.
 */
export interface StardustPack {
  id: string
  /** Price in USD cents (Stripe charges from this, never the client). */
  cents: number
  stardust: number
}

export const STARDUST_PACKS: readonly StardustPack[] = [
  { id: 'handful', cents: 199, stardust: 1400 },
  { id: 'pouch', cents: 499, stardust: 3900 },
  { id: 'chest', cents: 999, stardust: 8400 },
  { id: 'hoard', cents: 1999, stardust: 18000 },
]

export function stardustPackById(id: unknown): StardustPack | null {
  return STARDUST_PACKS.find(p => p.id === id) ?? null
}

// ── The wallet contract ─────────────────────────────────────────────────

/** One-time amnesty cap when importing a pre-ledger local balance. High
 * enough that no honest hoard is truncated (the full collection costs
 * ~64k), low enough that a pre-inflated localStorage can't poison the
 * ledger with nine digits. */
export const LEGACY_IMPORT_CAP = 200_000

/**
 * 🔒 v5.06 (security review): the migration amnesty — `legacy-import`
 * (stardust) and `/api/ownership/import` (collection) — trusts a
 * client-supplied pre-ledger snapshot ONCE per account. Genuine for real
 * pre-v4.92 players, but the server can't tell an honest hoard from an
 * injected one, so ANY fresh (or freshly-reset) account can claim up to
 * LEGACY_IMPORT_CAP stardust + a full collection for free.
 *
 * Worse than "once per account": a determined cheat spins up throwaway
 * ANONYMOUS accounts, imports 200k on each, and consolidates them into a
 * main account through the wallet transfer-code/redeem flow — an
 * UNBOUNDED faucet for as long as the amnesty is open. So the window is
 * kept SHORT: a courtesy for a genuine straggler, not a standing feature.
 * After this date both import paths reject and the exploit is dead.
 *
 * ⚠️ Set this to a PAST date to close the amnesty immediately (safe if
 * every real player has already migrated). It MUST be closed before live
 * Stripe keys go in — free amnesty stardust competes with paid packs.
 */
export const AMNESTY_CLOSES_MS = Date.parse('2026-08-22T00:00:00Z')

export function amnestyOpen(nowMs: number): boolean {
  return Number.isFinite(nowMs) && nowMs < AMNESTY_CLOSES_MS
}

/**
 * 🗃️ Match a batch's verdicts back to the events that were sent (v6.36).
 *
 * The outbox drops anything that came back settled and keeps what was
 * deferred. That only works if every verdict can be traced to its event:
 * before v6.36 a rejected one came back named `invalid:<i>`, matched
 * nothing, was never dropped, and flush() broke on it on every attempt
 * forever — with its estimate still showing in the header.
 *
 * Three ways in, most reliable first, because a client and a server can
 * be on different builds for as long as a tab stays open:
 *   index  — position in the request (v6.36 servers; always present)
 *   key    — the real tx key (works for anything already keyed)
 *   invalid:<n> — an older server's rejection; parse the index back out
 *
 * Nothing is ever dropped on a guess: an unmatched verdict leaves its
 * event queued, which is the safe direction (a stuck event costs a
 * retry, a wrongly dropped one costs the player real stardust).
 */
export function applyEarnResults(
  chunk: readonly { key: string }[],
  results: readonly { key?: string; index?: number; status?: string }[],
): { doneKeys: Set<string>; seen: Record<string, string> } {
  const doneKeys = new Set<string>()
  const seen: Record<string, string> = {}
  for (const r of results) {
    const legacy = typeof r.key === 'string' ? /^invalid:(\d+)$/.exec(r.key) : null
    const idx = typeof r.index === 'number' ? r.index
      : legacy ? Number(legacy[1])
        : -1
    const item = (idx >= 0 && idx < chunk.length)
      ? chunk[idx]
      : chunk.find(c => c.key === r.key)
    if (!item) continue
    seen[item.key] = String(r.status)
    if (r.status !== 'deferred') doneKeys.add(item.key)
  }
  return { doneKeys, seen }
}

/** Max events per POST /api/wallet/earn call; the client chunks. */
export const EARN_BATCH_MAX = 25

/** Per-uid, per-server-day faucet ceiling. 'act' caps CREDITED stardust
 * from act clears (~7 full A10 climbs — above any honest day). Overflow
 * is DEFERRED, not lost: the client keeps the event and retries next day.
 * (v5.06 dropped the 'refund' bucket with the booster-refund faucet.) */
export const DAY_CREDIT_CAPS = { act: 5000 } as const

/** How far back a dated event may reach (an offline backlog flushing in),
 * and how far forward (timezone ahead of UTC — same ±1 rule the daily
 * claim endpoint uses). */
export const EVENT_DAY_PAST_MS = 7 * 86400000
export const EVENT_DAY_FUTURE_MS = 86400000

export type EarnEvent =
  | { kind: 'act-clear'; runId: string; act: number; ascension: number }
  | { kind: 'ascension-first'; ascension: number }
  | { kind: 'puzzle-first'; level: number }
  | { kind: 'tutorial-first' }
  | { kind: 'daily-guess'; day: string; guessesUsed: number; solved: boolean }
  | { kind: 'daily-stat'; day: string }
  | { kind: 'daily-milestone'; day: string; mark: number }
  | { kind: 'legacy-import'; earned: number; spent: number }
  // NOTE: 'booster-refund' was RETIRED in v5.06. Packs roll and settle
  // their own refunds server-side (server/api/booster/open) since v4.93;
  // the earn-event path was dead client code but a live faucet — a POST
  // could mint ~112k/day of unearned refunds. Never reintroduce it.

export type SpendSku =
  | { kind: 'booster' }
  | { kind: 'creature'; rarity: string; defId?: string }

export function spendPrice(sku: SpendSku | null | undefined): number | null {
  if (!sku || typeof sku !== 'object') return null
  if (sku.kind === 'booster') return BOOSTER.price
  if (sku.kind === 'creature') {
    const price = CREATURE_PRICES[String((sku as { rarity?: unknown }).rarity)]
    return typeof price === 'number' ? price : null
  }
  return null
}

export interface EarnVerdict {
  ok: boolean
  /** Firestore tx doc id — existence is the once-only guard. */
  key?: string
  /** Server-computed credit. The client's estimate is display-only. */
  amount?: number
  /** Faucet bucket this event draws from, if capped. */
  bucket?: keyof typeof DAY_CREDIT_CAPS
  /** For first-clears: the bonusClaims (kind, key) to check-and-mark. */
  claim?: { kind: 'ascension-first' | 'puzzle-first' | 'tutorial-first'; key: number }
  /** For daily milestones: the mark, so the server can enforce that two
   * claims of the same mark sit at least `mark` days apart (the honest
   * minimum for a streak to re-land there after a reset). */
  milestoneMark?: number
  isImport?: boolean
  error?: string
}

/**
 * 🗝️ The tx key an event WOULD take, derived from shape alone (v6.36).
 *
 * A rejection has to be nameable or the client cannot drop it. Two
 * verdicts change with time rather than shape — a dated daily ages out
 * of dayOk's window, and legacy-import dies with the amnesty — so an
 * event queued while valid can come back rejected, and the outbox
 * needs to know WHICH one. Returns undefined when the event is too
 * malformed to name; the request index covers those.
 */
function derivableKey(e: Partial<EarnEvent> & { kind?: unknown }): string | undefined {
  const day = (e as { day?: unknown }).day
  const dated = typeof day === 'string' && DAY_RE.test(day) ? day : null
  switch (e.kind) {
    case 'daily-guess': return dated ? `dg:${dated}` : undefined
    case 'daily-stat': return dated ? `ds:${dated}` : undefined
    case 'daily-milestone': return dated ? `dm:${dated}` : undefined
    case 'legacy-import': return 'legacy-import'
    case 'tutorial-first': return 'tf'
    default: return undefined
  }
}

const ID_RE = /^[A-Za-z0-9_-]{4,64}$/
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

function intIn(v: unknown, min: number, max: number): number | null {
  const n = Number(v)
  return Number.isInteger(n) && n >= min && n <= max ? n : null
}

/** The `day` a dated event claims, validated against the server's clock:
 * the format must parse and sit within [−7 days, +1 day] of server UTC. */
function dayOk(day: unknown, serverNowMs: number): string | null {
  if (typeof day !== 'string' || !DAY_RE.test(day)) return null
  const t = Date.parse(`${day}T00:00:00Z`)
  if (!Number.isFinite(t)) return null
  const serverDay = Date.parse(new Date(serverNowMs).toISOString().slice(0, 10))
  if (t > serverDay + EVENT_DAY_FUTURE_MS || t < serverDay - EVENT_DAY_PAST_MS) return null
  return day
}

/**
 * Pure shape-and-amount validation for one earn event. State checks
 * (duplicate key, faucet bucket, milestone spacing, import-once) live in
 * the server transaction; everything computable without Firestore is
 * here so it can be unit-tested directly.
 */
export function validateEarnEvent(raw: unknown, serverNowMs: number): EarnVerdict {
  const e = raw as Partial<EarnEvent> & { kind?: unknown }
  if (!e || typeof e !== 'object') return { ok: false, error: 'not an event' }
  // v6.36: every rejection below carries the key it would have used,
  // where the shape allows one. `ok: false` still means "pay nothing"
  // — both callers gate on that first — but the client can now match
  // the verdict to the queued event and stop retrying it forever.
  const key = derivableKey(e)
  const no = (error: string): EarnVerdict => ({ ok: false, error, key })
  switch (e.kind) {
    case 'act-clear': {
      const ev = e as { runId?: unknown; act?: unknown; ascension?: unknown }
      const runId = typeof ev.runId === 'string' && ID_RE.test(ev.runId) ? ev.runId : null
      const act = intIn(ev.act, 1, 60)
      const ascension = intIn(ev.ascension, 0, 11)
      if (!runId || act === null || ascension === null) return no('bad act-clear')
      return { ok: true, key: `act:${runId}:${act}`, amount: stardustForAct(act, ascension), bucket: 'act' }
    }
    case 'ascension-first': {
      const ascension = intIn((e as { ascension?: unknown }).ascension, 0, 10)
      if (ascension === null) return no('bad ascension-first')
      return {
        ok: true, key: `af:${ascension}`, amount: firstClearStardust(ascension),
        claim: { kind: 'ascension-first', key: ascension },
      }
    }
    case 'puzzle-first': {
      const level = intIn((e as { level?: unknown }).level, 1, PUZZLE_LEVEL_COUNT)
      if (level === null) return no('bad puzzle-first')
      return {
        ok: true, key: `pf:${level}`, amount: puzzleClearStardust(level),
        claim: { kind: 'puzzle-first', key: level },
      }
    }
    case 'tutorial-first':
      // 🎓 v7.04: the First Dispatch's welcome - once per account, a booster's worth.
      return { ok: true, key: 'tf', amount: TUTORIAL_STARDUST, claim: { kind: 'tutorial-first', key: 1 } }
    case 'daily-guess': {
      const ev = e as { day?: unknown; guessesUsed?: unknown; solved?: unknown }
      const day = dayOk(ev.day, serverNowMs)
      const used = intIn(ev.guessesUsed, 1, DAILY_ECONOMY.guessRewards.length)
      if (!day || used === null || typeof ev.solved !== 'boolean') return no('bad daily-guess')
      return { ok: true, key: `dg:${day}`, amount: dailyGuessReward(used, ev.solved) }
    }
    case 'daily-stat': {
      const day = dayOk((e as { day?: unknown }).day, serverNowMs)
      if (!day) return no('bad daily-stat')
      return { ok: true, key: `ds:${day}`, amount: DAILY_ECONOMY.statBonus }
    }
    case 'daily-milestone': {
      const ev = e as { day?: unknown; mark?: unknown }
      const day = dayOk(ev.day, serverNowMs)
      const mark = Number(ev.mark)
      const amount = DAILY_ECONOMY.milestones[mark]
      if (!day || amount === undefined) return no('bad daily-milestone')
      return { ok: true, key: `dm:${day}`, amount, milestoneMark: mark }
    }
    case 'legacy-import': {
      // 🔒 v5.06: the amnesty self-closes (see AMNESTY_CLOSES_MS).
      if (!amnestyOpen(serverNowMs)) return no('amnesty closed')
      const ev = e as { earned?: unknown; spent?: unknown }
      const earned = intIn(ev.earned, 0, 2_000_000_000)
      const spent = intIn(ev.spent, 0, 2_000_000_000)
      if (earned === null || spent === null) return no('bad legacy-import')
      const amount = Math.min(Math.max(0, earned - spent), LEGACY_IMPORT_CAP)
      return { ok: true, key: 'legacy-import', amount, isImport: true }
    }
    default:
      return no('unknown kind')
  }
}
